import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApprovalPersonInterface } from "../../../../models/ProcurementModule/ApprovalPerson";
import { CACHE_KEY_APPROVALPERSONS } from "../../../../utils/constants";
import approvalPersonService from "../../../../services/procurementService/approvalPersonService";

interface UpdateApprovalPersonContext {
  previousApprovalPersons: ApprovalPersonInterface[];
}

const useUpdateApprovalPerson = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalPersonInterface,
    Error,
    ApprovalPersonInterface,
    UpdateApprovalPersonContext
  >({
    mutationFn: async (approvalPerson: ApprovalPersonInterface) => {
      // Assuming the ID is stored in the approvalPerson object or can be derived
      const { id, ...approvalPersonData } = approvalPerson;
      return approvalPersonService.Update(id || "", approvalPersonData);
    },

    onMutate: async (updatedApprovalPerson: ApprovalPersonInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_APPROVALPERSONS, page, pageSize],
      });

      const previousApprovalPersons =
        queryClient.getQueryData<ApprovalPersonInterface[]>([
          CACHE_KEY_APPROVALPERSONS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<ApprovalPersonInterface[]>(
        [CACHE_KEY_APPROVALPERSONS, page, pageSize],
        (approvalPersons = []) => {
          const updatedApprovalPersons = approvalPersons.map((approvalPerson) =>
            approvalPerson.id === updatedApprovalPerson.id
              ? updatedApprovalPerson
              : approvalPerson
          );
          return updatedApprovalPersons.sort((a, b) => {
            if (
              a.purchaseOrderType?.title &&
              b.purchaseOrderType?.title &&
              a.purchaseOrderType.title !== b.purchaseOrderType.title
            ) {
              return a.purchaseOrderType.title.localeCompare(
                b.purchaseOrderType.title
              );
            }
            return a.order - b.order;
          });
        }
      );

      return { previousApprovalPersons };
    },

    onSuccess: (savedApprovalPerson) => {
      queryClient.setQueryData<ApprovalPersonInterface[]>(
        [CACHE_KEY_APPROVALPERSONS, page, pageSize],
        (approvalPersons) => {
          const updatedApprovalPersons =
            approvalPersons?.map((approvalPerson) =>
              approvalPerson.id === savedApprovalPerson.id
                ? savedApprovalPerson
                : approvalPerson
            ) ?? [];

          return updatedApprovalPersons.sort((a, b) => {
            if (
              a.purchaseOrderType?.title &&
              b.purchaseOrderType?.title &&
              a.purchaseOrderType.title !== b.purchaseOrderType.title
            ) {
              return a.purchaseOrderType.title.localeCompare(
                b.purchaseOrderType.title
              );
            }
            return a.order - b.order;
          });
        }
      );
    },

    onError: (error, updatedApprovalPerson, context) => {
      if (!context) return;

      queryClient.setQueryData<ApprovalPersonInterface[]>(
        [CACHE_KEY_APPROVALPERSONS, page, pageSize],
        context.previousApprovalPersons
      );
    },
  });
};

export default useUpdateApprovalPerson;
